import { useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  CandidateStaffingIssue,
  StaffingAnalysis,
  StaffingFinding,
  StaffingFindingSeverity,
  StaffingRoleAssessment,
} from "../../types"

import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Users,
  XCircle,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import { STAFFING_FINDING_TYPE_LABEL_KEY } from "./bid-review-constants"

// ── Types ─────────────────────────────────────────────────────────────────────

type StaffingTabProps = {
  staffingPlanIncluded?: boolean
  staffingAnalysis?: StaffingAnalysis
  isLoading: boolean
}

// ── Config maps ───────────────────────────────────────────────────────────────

const COVERAGE_CONFIG: Record<
  "full" | "partial" | "unstaffed",
  { labelKey: string; badgeClass: string; icon: React.ElementType; iconClass: string }
> = {
  full: {
    labelKey: "tender.bidReview.staffing.coverage.full",
    badgeClass: "border-success bg-success/10 text-success",
    icon: CheckCircle2,
    iconClass: "text-success",
  },
  partial: {
    labelKey: "tender.bidReview.staffing.coverage.partial",
    badgeClass: "border-warning bg-warning/10 text-warning-foreground",
    icon: AlertTriangle,
    iconClass: "text-warning",
  },
  unstaffed: {
    labelKey: "tender.bidReview.staffing.coverage.unstaffed",
    badgeClass: "border-destructive bg-destructive/10 text-destructive",
    icon: XCircle,
    iconClass: "text-destructive",
  },
}

const RISK_LEVEL_CONFIG: Record<
  "low" | "medium" | "high" | "critical",
  { labelKey: string; bannerClass: string; badgeClass: string }
> = {
  low: {
    labelKey: "tender.bidReview.staffing.riskLevel.low",
    bannerClass: "border-success bg-success/15",
    badgeClass: "border-success bg-success/10 text-success",
  },
  medium: {
    labelKey: "tender.bidReview.staffing.riskLevel.medium",
    bannerClass: "border-warning bg-warning/10",
    badgeClass: "border-warning bg-warning/10 text-warning-foreground",
  },
  high: {
    labelKey: "tender.bidReview.staffing.riskLevel.high",
    bannerClass: "border-primary-ink bg-primary/10",
    badgeClass: "border-primary-ink bg-primary/10 text-primary-ink",
  },
  critical: {
    labelKey: "tender.bidReview.staffing.riskLevel.critical",
    bannerClass: "border-destructive/30 bg-destructive/10",
    badgeClass: "border-destructive bg-destructive/10 text-destructive",
  },
}

const CANDIDATE_RISK_CONFIG: Record<
  "low" | "medium" | "high",
  { labelKey: string; badgeClass: string }
> = {
  low: {
    labelKey: "tender.bidReview.staffing.candidateRisk.low",
    badgeClass: "border-success bg-success/10 text-success",
  },
  medium: {
    labelKey: "tender.bidReview.staffing.candidateRisk.medium",
    badgeClass: "border-warning bg-warning/10 text-warning-foreground",
  },
  high: {
    labelKey: "tender.bidReview.staffing.candidateRisk.high",
    badgeClass: "border-destructive bg-destructive/10 text-destructive",
  },
}

const SEVERITY_CONFIG: Record<StaffingFindingSeverity, { labelKey: string; badgeClass: string }> = {
  critical: {
    labelKey: "tender.bidReview.severity.critical",
    badgeClass: "border-destructive bg-destructive/10 text-destructive",
  },
  major: {
    labelKey: "tender.bidReview.severity.major",
    badgeClass: "border-primary-ink bg-primary/10 text-primary-ink",
  },
  minor: {
    labelKey: "tender.bidReview.severity.minor",
    badgeClass: "border-warning bg-warning/10 text-warning-foreground",
  },
  info: {
    labelKey: "tender.bidReview.severity.info",
    badgeClass: "border-accent bg-accent/50 text-accent-foreground",
  },
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FindingRow({ finding }: { finding: StaffingFinding }) {
  const { t } = useTranslation()
  const sevConf = SEVERITY_CONFIG[finding.severity]
  return (
    <div className="bg-muted/20 flex flex-col gap-1 rounded-lg border px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-medium">
          {t(STAFFING_FINDING_TYPE_LABEL_KEY[finding.findingType])}
        </span>
        <Badge className={cn("h-4 px-1.5 text-[10px]", sevConf.badgeClass)} variant="outline">
          {t(sevConf.labelKey)}
        </Badge>
      </div>
      <p className="text-muted-foreground text-[12px] leading-relaxed">{finding.description}</p>
      {finding.affectedCandidates.length > 0 && (
        <p className="text-muted-foreground text-[11px]">
          {t("tender.bidReview.staffing.affectedCandidates", {
            list: finding.affectedCandidates.join(", "),
          })}
        </p>
      )}
    </div>
  )
}

function CandidateIssueRow({ issue }: { issue: CandidateStaffingIssue }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const riskConf = CANDIDATE_RISK_CONFIG[issue.riskLevel]
  const hasDetails =
    issue.missingHardRequirements.length > 0 ||
    issue.missingSoftRequirements.length > 0 ||
    issue.overqualifiedRequirements.length > 0 ||
    Boolean(issue.note)

  let expandIcon: React.ReactNode = <span className="size-3.5 shrink-0" />
  if (hasDetails) {
    expandIcon = expanded ? (
      <ChevronDown className="text-muted-foreground size-3.5 shrink-0" />
    ) : (
      <ChevronRight className="text-muted-foreground size-3.5 shrink-0" />
    )
  }

  return (
    <div className="rounded-lg border">
      <Button
        disabled={!hasDetails}
        type="button"
        variant="ghost"
        className={cn(
          "h-auto w-full justify-start gap-2 px-3 py-2 text-left font-normal",
          hasDetails && "hover:bg-muted/30",
          !hasDetails && "cursor-default"
        )}
        onClick={() => hasDetails && setExpanded((v) => !v)}
      >
        {expandIcon}
        <span className="min-w-0 flex-1 text-[13px] font-medium">{issue.candidateName}</span>
        <Badge
          className={cn("h-4 shrink-0 px-1.5 text-[10px]", riskConf.badgeClass)}
          variant="outline"
        >
          {t(riskConf.labelKey)}
        </Badge>
      </Button>
      {expanded && hasDetails && (
        <div className="border-t px-3 pt-2 pb-2.5">
          {issue.missingHardRequirements.length > 0 && (
            <div className="mb-1.5">
              <p className="text-destructive mb-1 text-[11px] font-semibold tracking-wide uppercase">
                {t("tender.bidReview.staffing.missingHardRequirements")}
              </p>
              <div className="flex flex-wrap gap-1">
                {issue.missingHardRequirements.map((req) => (
                  <Badge
                    key={req}
                    className="border-destructive bg-destructive/10 text-destructive text-[10px]"
                    variant="outline"
                  >
                    {req}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {issue.missingSoftRequirements.length > 0 && (
            <div className="mb-1.5">
              <p className="text-warning-foreground mb-1 text-[11px] font-semibold tracking-wide uppercase">
                {t("tender.bidReview.staffing.missingSoftRequirements")}
              </p>
              <div className="flex flex-wrap gap-1">
                {issue.missingSoftRequirements.map((req) => (
                  <Badge
                    key={req}
                    className="border-warning bg-warning/10 text-warning-foreground text-[10px]"
                    variant="outline"
                  >
                    {req}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {issue.overqualifiedRequirements.length > 0 && (
            <div className="mb-1.5">
              <p className="text-accent-foreground mb-1 text-[11px] font-semibold tracking-wide uppercase">
                {t("tender.bidReview.staffing.overqualifiedRequirements")}
              </p>
              <div className="flex flex-wrap gap-1">
                {issue.overqualifiedRequirements.map((req) => (
                  <Badge
                    key={req}
                    className="border-accent bg-accent/50 text-accent-foreground text-[10px]"
                    variant="outline"
                  >
                    {req}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {issue.note && (
            <p className="text-muted-foreground text-[12px] leading-relaxed">{issue.note}</p>
          )}
        </div>
      )}
    </div>
  )
}

function RoleCard({ role }: { role: StaffingRoleAssessment }) {
  const { t } = useTranslation()
  const coverageConf = COVERAGE_CONFIG[role.coverageStatus]
  const CoverageIcon = coverageConf.icon

  return (
    <div className="rounded-lg border">
      {/* Role header */}
      <div className="flex items-start gap-3 border-b px-4 py-3">
        <CoverageIcon className={cn("mt-0.5 size-4 shrink-0", coverageConf.iconClass)} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold">{role.roleName}</span>
            <span className="text-muted-foreground font-mono text-[11px]">{role.roleId}</span>
            <Badge
              className={cn("h-4 px-1.5 text-[10px]", coverageConf.badgeClass)}
              variant="outline"
            >
              {t(coverageConf.labelKey)}
            </Badge>
            <span className="text-muted-foreground text-[11px]">
              {role.quantityAssigned} av {role.quantityRequired} tillsatta
            </span>
          </div>
          {role.overallAssessment && (
            <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">
              {role.overallAssessment}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3 p-3">
        {/* Role findings */}
        {role.roleFindings.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
              {t("tender.bidReview.staffing.findingsSection")}
            </p>
            {role.roleFindings.map((finding, i) => (
              <FindingRow key={i} finding={finding} />
            ))}
          </div>
        )}

        {/* Candidate issues */}
        {role.candidateIssues.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
              {t("tender.bidReview.staffing.candidatesSection")}
            </p>
            {role.candidateIssues.map((issue, i) => (
              <CandidateIssueRow key={i} issue={issue} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Loading skeleton ───────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-6 w-48" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    </div>
  )
}

// ── Empty state (no staffing plan) ────────────────────────────────────────────

function NoStaffingPlan() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <div className="bg-muted flex size-10 items-center justify-center rounded-full">
        <Users className="text-muted-foreground size-5" />
      </div>
      <div>
        <p className="text-[13px] font-medium">{t("tender.bidReview.staffingTab.noPlan")}</p>
        <p className="text-muted-foreground mt-1 text-[12px] leading-relaxed">
          {t("tender.bidReview.staffingTab.emptyHint")}
        </p>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function StaffingTab({
  staffingPlanIncluded,
  staffingAnalysis,
  isLoading,
}: StaffingTabProps) {
  const { t } = useTranslation()
  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (!staffingPlanIncluded || !staffingAnalysis) {
    return <NoStaffingPlan />
  }

  const riskConf = RISK_LEVEL_CONFIG[staffingAnalysis.staffingRiskLevel]

  return (
    <div className="flex flex-col gap-5">
      {/* Overall risk banner */}
      <div className={cn("rounded-lg border p-4", riskConf.bannerClass)}>
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[12px] font-semibold tracking-wide uppercase">
            {t("tender.bidReview.staffing.staffingRiskBanner")}
          </span>
          <Badge className={cn("h-4 px-1.5 text-[10px]", riskConf.badgeClass)} variant="outline">
            {t(riskConf.labelKey)}
          </Badge>
        </div>
        <p className="text-[13px] leading-relaxed">{staffingAnalysis.overallStaffingSummary}</p>
      </div>

      {/* Recommended actions */}
      {staffingAnalysis.recommendedActions.length > 0 && (
        <section>
          <h4 className="mb-2 text-[12px] font-semibold tracking-wide uppercase">
            {t("tender.bidReview.staffing.recommendedActions")}
          </h4>
          <ul className="flex flex-col gap-1.5">
            {staffingAnalysis.recommendedActions.map((action, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px]">
                <span className="text-muted-foreground mt-0.5 min-w-[18px] shrink-0 text-[12px] font-bold tabular-nums">
                  {i + 1}.
                </span>
                {action}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Per-role cards */}
      {staffingAnalysis.roleAssessments.length > 0 && (
        <section>
          <h4 className="mb-2 text-[12px] font-semibold tracking-wide uppercase">
            {t("tender.bidReview.staffing.rolesSection")}
          </h4>
          <div className="flex flex-col gap-3">
            {staffingAnalysis.roleAssessments.map((role) => (
              <RoleCard key={role.roleId} role={role} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
